import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import SensorPage from "./pages/SensorPage/SensorPage";
import DevicePage from "./pages/DevicePage/DevicePage";
import MeasurementRunPage from "./pages/MeasurementRunPage/MeasurementRunPage";
import Home from "./pages/Home/Home";
import Log from "./pages/Log/Log";
import MeasurementCategoriesPage from "./pages/MeasurementCategoriesPage/MeasurementCategoriesPage";
import MeasurementDetailLayout from "./pages/MeasurementDetailLayout/MeasurementDetailLayout";
import MeasurementDetails from "./pages/MeasurementDetails/MeasurementDetails";
import RawDataView from "./pages/RawDataView/RawDataView";
import GraphPage from "./pages/GraphPage/GraphPage";
import Login from "./pages/LoginPage/Login";
import UserProfile from "./components/Tables/UserProfile";
import UserDashboard from "./components/Tables/UserDashboard";
import UserControl from "./components/Tables/UserControl";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const App = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [auth, setAuth] = useState({
        isAuthenticated: false,
        user: null,
        token: null
    });
    const navigate = useNavigate();

    // Check authentication
    useEffect(() => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        if (token && user) {
            setAuth({
                isAuthenticated: true,
                user: JSON.parse(user),
                token
            });
        }
        setLoading(false);
    }, []);

    const fetchMeasurementCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/measurement-categories`, {
                headers: { Authorization: `Bearer ${auth.token}`}
            });

            setCategories(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            }
        }
    };

    const handleLogin = (userData, token) => {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        setAuth({
            isAuthenticated: true,
            user: userData,
            token
        });
        navigate('/');
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setAuth({
            isAuthenticated: false,
            user: null,
            token: null
        });
        navigate("/login");
    };

    useEffect(() => {
        if (auth.isAuthenticated) {
            fetchMeasurementCategories();
        }
    }, [auth.isAuthenticated]);

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>;
    }

    // Protected route component
    const ProtectedRoute = ({ children, requiredRole = "user" }) => {
        if (!auth.isAuthenticated) {
            return <Navigate to="/login" replace />;
        }

        if (requiredRole === "admin" && auth.user?.role !== 'admin') {
            return <Navigate to="/dashboard" replace />; // Redirect non-admins to dashboard
        }

        return children;
    };

    return (
        <Routes>
            <Route path="/login" element={
                auth.isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            } />
            <Route path="/profile" element={
                <ProtectedRoute>
                    <UserProfile auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/" element={
                <ProtectedRoute>
                    <Home auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <UserDashboard auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/user-control" element={
                <ProtectedRoute requiredRole="admin">
                    <UserControl auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/rawTable" element={
                <ProtectedRoute>
                    <Log auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/sensor" element={
                <ProtectedRoute>
                    <SensorPage auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/device" element={
                <ProtectedRoute>
                    <DevicePage auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/measurement-categories" element={
                <ProtectedRoute>
                    <MeasurementCategoriesPage
                        categories={categories}
                        fetchCategories={fetchMeasurementCategories}
                        auth={auth}
                        onLogout={handleLogout}
                    />
                </ProtectedRoute>
            } />
            <Route path="/measurement" element={
                <ProtectedRoute>
                    <MeasurementRunPage categories={categories} auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            } />
            <Route path="/measurement/:id" element={
                <ProtectedRoute>
                    <MeasurementDetailLayout auth={auth} onLogout={handleLogout} />
                </ProtectedRoute>
            }>
                <Route index element={<MeasurementDetails />} />
                <Route path="details" element={<MeasurementDetails />} />
                <Route path="raw" element={<RawDataView />} />
                <Route path="graph" element={<GraphPage auth={auth} onLogout={handleLogout} />} />
            </Route>
        </Routes>
    );
};

export default App;